import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  handleErrorToast,
  handleSuccessToast,
} from 'src/components/molecules/Toast';
import {
  CaseCustomFieldValueType,
  useCaseCustomFieldValuesQuery,
  useGetOrganisationCustomFieldSectionsQuery,
  useSetCaseCustomFieldValueMutation,
} from 'src/graphql';
import * as styles from './CaseCustomFields.styles';

const UNGROUPED_KEY = '__ungrouped__';

interface FieldGroup {
  key: string;
  name: string | null;
  order: number;
  fields: CaseCustomFieldValueType[];
}

// Groups case fields under their section, ordered by the section displayOrder,
// with ungrouped fields last. Fields keep their own displayOrder within a group.
const groupFieldsBySection = (
  fields: CaseCustomFieldValueType[],
  sections: Array<{ id: string; name: string; displayOrder: number }>,
): FieldGroup[] => {
  const sectionById = new Map(sections.map((section) => [section.id, section]));
  const groupsByKey = new Map<string, FieldGroup>();

  for (const field of fields) {
    const section = field.sectionId ? sectionById.get(field.sectionId) : null;
    const key = section?.id ?? UNGROUPED_KEY;

    let group = groupsByKey.get(key);
    if (!group) {
      group = {
        key,
        name: section?.name ?? null,
        // Ungrouped sorts after every real section.
        order: section?.displayOrder ?? Number.MAX_SAFE_INTEGER,
        fields: [],
      };
      groupsByKey.set(key, group);
    }
    group.fields.push(field);
  }

  return [...groupsByKey.values()].sort((a, b) => a.order - b.order);
};

const FIELD_TYPE = {
  TEXT: 'TEXT',
  SINGLE_SELECT: 'SINGLE_SELECT',
} as const;

const FIELD_STATUS_ACTIVE = 'ACTIVE';

interface Props {
  caseId: string;
}

// Controlled text input: mirrors the persisted value into local state so that a
// refetch after save (or an external change) updates the field, unlike an
// uncontrolled defaultValue which only applies on mount.
const TextValueInput = ({
  customFieldId,
  value,
  label,
  onSave,
  isReadOnly = false,
}: {
  customFieldId: string;
  value: string;
  label?: string;
  onSave: (customFieldId: string, valueText: string) => void;
  isReadOnly?: boolean;
}) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <TextField
      size="medium"
      label={label ?? ''}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        if (draft !== value) {
          onSave(customFieldId, draft);
        }
      }}
      slotProps={{
        htmlInput: {
          'data-testid': `case-custom-field-text-${customFieldId}`,
        },
        input: { readOnly: isReadOnly },
      }}
    />
  );
};

const renderField = (
  fieldValue: CaseCustomFieldValueType,
  onSaveText: (customFieldId: string, valueText: string) => void,
  onSaveOption: (customFieldId: string, optionId: string) => void,
) => {
  const selectedOption = fieldValue.options.find(
    (option) => option.id === fieldValue.optionId,
  );

  // A retired field, or a selected option that has since been retired, is shown
  // read-only: the value stays visible but can no longer be edited (the backend
  // rejects edits to non-active fields too).
  const isReadOnly =
    fieldValue.status !== FIELD_STATUS_ACTIVE ||
    (selectedOption != null && !selectedOption.isActive);

  if (fieldValue.fieldType === FIELD_TYPE.TEXT) {
    return (
      <TextValueInput
        customFieldId={fieldValue.customFieldId}
        value={fieldValue.valueText ?? ''}
        label={fieldValue.label}
        onSave={onSaveText}
        isReadOnly={isReadOnly}
      />
    );
  }

  if (fieldValue.fieldType === FIELD_TYPE.SINGLE_SELECT) {
    const labelId = `case-custom-field-label-${fieldValue.customFieldId}`;
    const visibleOptions = isReadOnly
      ? fieldValue.options.filter(
          (o) => o.isActive || o.id === fieldValue.optionId,
        )
      : fieldValue.options.filter((o) => o.isActive);
    return (
      <FormControl fullWidth>
        <FormLabel id={labelId}>{fieldValue.label}</FormLabel>
        <Select
          value={fieldValue.optionId ?? ''}
          displayEmpty
          labelId={labelId}
          onChange={(event: SelectChangeEvent) =>
            onSaveOption(fieldValue.customFieldId, event.target.value)
          }
          data-testid={`case-custom-field-select-${fieldValue.customFieldId}`}
          slotProps={{
            input: { readOnly: isReadOnly },
          }}
          sx={isReadOnly ? styles.readOnlyValue : undefined}
        >
          <MenuItem value="">
            <em>Not set</em>
          </MenuItem>
          {visibleOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return null;
};

export const CaseCustomFields = ({ caseId }: Props) => {
  const { data, refetch } = useCaseCustomFieldValuesQuery({ id: caseId });
  const { data: sectionsData } = useGetOrganisationCustomFieldSectionsQuery();
  const setValue = useSetCaseCustomFieldValueMutation();

  const [fields, setFields] = useState<CaseCustomFieldValueType[]>([]);

  useEffect(() => {
    setFields(data?.caseById?.CaseCustomFieldValues ?? []);
  }, [data]);

  const handleError = (error: unknown) => {
    const code = (error as { code?: string } | null)?.code;
    const isConflict = code === 'CONFLICT';

    if (isConflict) {
      handleErrorToast({
        toastMessage: 'This field changed',
        toastDescription:
          'The field configuration changed. Refreshing the latest options.',
      });
      void refetch();
      return;
    }

    handleErrorToast({
      toastMessage: 'Could not save',
      toastDescription: 'Unable to update the custom field. Please try again.',
    });
  };

  const save = (
    customFieldId: string,
    payload: { valueText?: string | null; optionId?: string | null },
  ) => {
    setValue.mutate(
      { input: { caseId, customFieldId, ...payload } },
      {
        onSuccess: () => {
          handleSuccessToast({ toastMessage: 'Custom field updated' });
          void refetch();
        },
        onError: handleError,
      },
    );
  };

  if (fields.length === 0) {
    return null;
  }

  const sections =
    sectionsData?.getOrganisationCustomFieldSections?.sections ?? [];
  const groups = groupFieldsBySection(fields, sections);

  return (
    <Box sx={styles.container} data-testid="case-custom-fields">
      <Box sx={styles.sectionCard}>
        <Typography sx={styles.title}>Custom fields</Typography>
        {groups.map((section) => (
          <Accordion
            key={section.key}
            data-testid={`case-custom-field-section-${section.key}`}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls={`panel${section.key}-content`}
              id={`panel${section.key}-header`}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#000000',
                }}
              >
                {section.name}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={styles.grid}>
                {section.fields.map((fieldValue) => (
                  <Box
                    key={fieldValue.customFieldId}
                    sx={styles.field}
                    data-testid={`case-custom-field-${fieldValue.customFieldId}`}
                  >
                    {renderField(
                      fieldValue,
                      (customFieldId, valueText) =>
                        save(customFieldId, { valueText }),
                      (customFieldId, optionId) =>
                        save(customFieldId, { optionId }),
                    )}
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};
